<template>
  <div>
    <header v-if="shouldShowHeader" class="otb-header">
      <div ref="otbHeader" class="otb-main-header-container" :class="dynamicClass('otb-main-header-container')">
        <div class="otb-main-header-content" :class="dynamicClass('otb-main-header-content')">
          <div class="otb-main-header-content__top" :class="dynamicClass('otb-main-header-content__top')">
            <div class="otb-main-header-content__top-link-container" v-if="shouldShowReturnToModelLink">
              <wb-icon class="otb-main-header-content__top-icon" flip-rtl name="bds/chevron-left/24"></wb-icon>
              <button
                type="button"
                @click="goToHomeEvent"
                class="wb-button wb-button--tertiary wb-button--small otb-main-header-content__top-link-container__link"
              >
                {{ goToHome }}
              </button>
            </div>

            <span class="otb-main-header-content__top-container">
              <h1 class="otb-main-header-content__top-container-title wb-type-heading-l">
                {{ title }}
              </h1>
              <wb-tooltip-host v-if="shouldShowHeaderTooltip" :position="tooltipPosition">
                <wb-icon class="otb-main-header-content__top-title-icon" name="bds/info-circle/16"></wb-icon>
                <wb-tooltip>
                  <p class="otb-main-header-content__top-tooltip-title wb-type-copy-tertiary">
                    {{ $t('header.tooltip.title') }}
                  </p>
                  <p class="otb-main-header-content__top-tooltip-text wb-type-hint">
                    {{ $t('header.tooltip.text') }}
                  </p>
                </wb-tooltip>
              </wb-tooltip-host>
            </span>
          </div>
          <div class="otb-main-header-content__bottom" :class="dynamicClass('otb-main-header-content__bottom')">
            <p
              class="otb-main-header-content__bottom-model wb-type-heading-s"
              :class="dynamicClass('otb-main-header-content__bottom-model')"
            >
              {{ modelName }}
            </p>
            <img
              v-if="modelName"
              :src="vehicleImageSm"
              alt="Vehicle Image"
              class="otb-main-header-content__bottom-image otb-main-header-content__bottom-image--sm"
              :class="[
                dynamicClass('otb-main-header-content__bottom-image'),
                { 'otb-main-header-content__bottom-image--hidden': !showVehicleSmallImage },
              ]"
            />
            <img
              v-if="modelName"
              :src="vehicleImageLg"
              alt="Vehicle Image"
              class="otb-main-header-content__bottom-image otb-main-header-content__bottom-image--lg"
              :class="dynamicClass('otb-main-header-content__bottom-image')"
            />
          </div>
        </div>
      </div>
      <div
        class="otb-header-search"
        ref="otbSearchBar"
        :class="dynamicClass('otb-header-search')"
        :style="navTopStyle"
        v-if="shouldShowSearch"
      >
        <button
          :class="[
            dynamicClass('otb-header-search__content__search-result'),
            { 'otb-header-search__content__request': isInRequestPage },
          ]"
          class="otb-header-search__vehicle-preferences__button wb-button wb-button--tertiary wb-button--large"
          v-if="shouldShowVehiclePreferencesButton"
          @click="toggleFiltersModalAndMenu()"
        >
          <div
            class="otb-header-search__content__search-result__filter otb-header-search__content__search-result__filter__no_margin"
          >
            <wb-icon name="bds/pencil/24" class="otb-header-search__vehicle-preferences__button__icon"></wb-icon>
            {{ $t('shared.vehiclePreferences.button') }}
          </div>
        </button>

        <div
          class="otb-header-search__content"
          :class="[
            dynamicClass('otb-header-search__content'),
            { 'otb-header-search__content__request': isInRequestPage },
          ]"
          v-if="shouldShowSearchContentLogic"
        >
          <div class="otb-header-search__inputs">
            <location-box
              v-if="!hasFixedDealer"
              class="otb-location-box"
              :validation="locationInputRoute"
              :hideTitle="!expandedMode"
            />
            <span v-else class="dealer-info">
              <button type="button" @click="onReturn" class="dealer-info__button">
                <wb-icon class="dealer-info__icon" flip-rtl name="bds/chevron-left/24"></wb-icon>
                {{ $t('header.return') }}
              </button>
              <p class="dealer-info__name wb-type-heading-s">{{ dealerName }}</p>
              <span class="dealer-info__divider"></span>
            </span>

            <div class="otb-datetime-box">
              <h3 class="wb-type-copy-strong" :style="hiddenTitle">
                <wb-icon name="time-xs"></wb-icon>
                {{ $t('header.date.search.description') }}
              </h3>
              <datetime-picker
                :activeTextCarouselSlide="activeCalendarSlide"
                :dealerCalendar="dealerCalendar"
                :labelPlaceholder="$t('schedule.filtersMenu.mobile.date.input.label')"
                :locale="localeCode"
                :isOptional="true"
                :isMobile="isHandheldDevice"
                :calendarType="calendarType"
                :selectedDates="selectedDate"
                @changeDate="changeDate"
                @changeSheet="changeSheet"
              />
            </div>
          </div>

          <div
            :class="dynamicClass('otb-header-search__content__search-result')"
            class="otb-header-search__content__search-result"
            v-if="noBookingResults || !bookingActive"
          >
            <button
              v-if="shouldShowDealerNameOnBottom"
              @click="toggleSectionOnClick()"
              class="otb-header-search__content__search-result__filter"
            >
              <wb-icon name="bds/location/24" class="otb-header-search__content__search-result__filter__icon"></wb-icon>
              {{ dealerName }}
            </button>
            <button
              v-if="showVehicleDetails"
              @click="toggleSectionOnClick()"
              class="otb-header-search__content__search-result__filter"
            >
              <wb-icon
                name="bds/car/24"
                class="otb-header-search__content__search-result__filter__icon otb-header-search__content__search-result__filter__icon--big"
              ></wb-icon>
              {{ filtersSelectedString }}
            </button>
            <template v-else>
              <button
                v-if="!hasFixedDealer"
                @click="filtersButtonEnable && toggleSectionOnClick()"
                class="otb-header-search__content__search-result__filter"
              >
                <wb-icon
                  name="bds/location/24"
                  class="otb-header-search__content__search-result__filter__icon"
                ></wb-icon>
                "{{ locationSelected }}"
              </button>
              <button
                @click="toggleSectionOnClick()"
                v-if="showDate"
                class="otb-header-search__content__search-result__filter otb-header-search__content__search-result__filter--date"
              >
                <wb-icon
                  name="time-xs"
                  class="otb-header-search__content__search-result__filter__icon otb-header-search__content__search-result__filter__icon--small"
                ></wb-icon>
                {{ dateSelected }}
              </button>
            </template>
          </div>

          <template v-else>
            <div
              :class="dynamicClass('otb-header-search__content__search-result')"
              class="otb-header-search__content__search-result"
            >
              <button
                v-if="selectedInfo"
                @click="toggleSectionOnClick()"
                class="otb-header-search__content__search-result__filter"
              >
                <wb-icon
                  name="bds/location/24"
                  class="otb-header-search__content__search-result__filter__icon"
                ></wb-icon
                >{{ selectedInfo }}
              </button>
              <button
                v-if="showDate"
                @click="toggleSectionOnClick()"
                class="otb-header-search__content__search-result__filter otb-header-search__content__search-result__filter--date"
              >
                <wb-icon
                  name="time-xs"
                  class="otb-header-search__content__search-result__filter__icon otb-header-search__content__search-result__filter__icon--small"
                ></wb-icon>
                {{ dateSelected }}
              </button>
            </div>
          </template>

          <div class="otb-header-search__actions">
            <button
              v-if="!showDetailsButton"
              href="#"
              :disabled="filtersButtonStatus"
              class="otb-filters-button wb-button wb-button--tertiary wb-button--large"
              :class="dynamicClass('otb-filters-button')"
              @click="toggleSectionOnClick()"
            >
              <wb-icon class="wb-button__icon" aria-hidden="true" flip-rtl name="bds/filter/24"></wb-icon
              >{{ isHandheldDevice ? $t('header.filters.cta.mobile') : $t('header.filters.cta') }}
              <wb-counter
                v-if="filterCounter"
                class="otb-filters-button__counter"
                :value="filterCounter"
                mode="primary"
              ></wb-counter>
            </button>
            <button
              v-else
              href="#"
              :disabled="filtersButtonStatus"
              class="otb-details-button wb-button wb-button--tertiary wb-button--large"
              :class="dynamicClass('otb-details-button')"
              @click="toggleDetailsOrFiltersModal()"
            >
              <wb-icon
                class="otb-details-button otb-details-button__icon"
                aria-hidden="true"
                flip-rtl
                :name="searchActionsIcon"
              ></wb-icon>
              <span v-if="!isInRequestPage">{{ $t('header.details.cta') }}</span>
              <span v-else>{{ $t('shared.editVehiclePreferences.button') }}</span>
            </button>
            <button
              v-if="expandedMode"
              @click="selectLocation"
              class="otb-search-button wb-button wb-button--primary wb-button--large"
              :class="dynamicClass('otb-search-button')"
              :disabled="!findBookingsButtonEnable"
            >
              <div v-if="isTestDriveFromHomeEnabled">{{ $t('header.cta.proceed') }}</div>
              <div v-else>
                <wb-icon
                  v-if="isBookingActive"
                  class="wb-button__icon"
                  aria-hidden="true"
                  flip-rtl
                  name="bds/search/16"
                ></wb-icon
                >{{ headerCta }}
              </div>
            </button>
          </div>
        </div>
        <div class="otb-header-search__mobile-actions" :style="footerTopStyle">
          <div class="wb-grid-container">
            <div class="wb-grid-row">
              <div class="wb-grid-col-mq1-12">
                <button
                  v-if="expandedMode"
                  @click="selectLocation"
                  class="otb-search-button wb-button wb-button--primary wb-button--large"
                  :class="dynamicClass('otb-search-button')"
                  :disabled="!findBookingsButtonEnable"
                >
                  <div v-if="isTestDriveFromHomeEnabled">{{ $t('header.cta.proceed') }}</div>
                  <div v-else>
                    <wb-icon
                      v-if="isBookingActive"
                      class="wb-button__icon"
                      aria-hidden="true"
                      flip-rtl
                      name="bds/search/16"
                    ></wb-icon
                    >{{ headerCta }}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="shouldShowBackToResultsLink" class="otb-header-return" ref="otbHeaderReturn">
        <div class="wb-grid-container otb-header-return__container">
          <button class="otb-header-return__button wb-button wb-button--tertiary wb-button--small" @click="onReturn">
            <wb-icon class="otb-header-return__button-icon" aria-hidden="true" flip-rtl name="chevron-left"></wb-icon>
            <span class="wb-type-copy-secondary">{{ backButtonText }} </span>
          </button>
        </div>
      </div>

      <div
        v-if="shouldShowDealerHeader"
        class="otb-header-return otb-header-return--fixed"
        ref="otbHeaderReturn"
        :style="navTopStyleFixedDealer"
      >
        <div class="wb-grid-container otb-header-return__container">
          <button class="otb-header-return__button wb-button wb-button--tertiary wb-button--small" @click="onReturn">
            <wb-icon class="otb-header-return__button-icon" aria-hidden="true" flip-rtl name="chevron-left"></wb-icon>
            <span v-if="!isSmallScreen" class="wb-type-copy-secondary">{{ $t('header.return') }}</span>
          </button>
          <span class="wb-type-heading-xs otb-header-return__dealer">{{ dealerName }}</span>
        </div>
      </div>
    </header>
    <filters-menu
      v-if="showFiltersMenu"
      :selectedDates="selectedDate"
      @changeSelectedDate="changeDate"
      @dismiss="toggleFiltersMenu(false)"
    />
    <filters-modal
      v-if="showFiltersModalState"
      :selectedDates="selectedDate"
      @changeSelectedDate="changeDate"
      @dismiss="toggleFiltersModalAndMenu()"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, onUpdated, watch, set } from 'vue';
import { i18n } from '@dh-io-otb/shared/src/services/translation.service';

import FiltersMenu from '../filters-menu/filters-menu.vue';
import FiltersModal from '../filters-modal/filters-modal.vue';

import { DatetimePicker, LocationBox } from '@dh-io-otb/shared/src/components/index';

import { useNav } from '../../composables/nav.composable';
import { useFooterPosition } from '../../composables/footer.composable';
import { useRoutingApplication } from '../../composables/routing-application.composable';
import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';
import { useUtils } from '@dh-io-otb/shared/src/composables/utils.composable';
import { useMediaQuery } from '@dh-io-otb/shared/src/composables/media-query.composable';
import { useMainHeader } from '../../composables/main-header.composable';
import useRequestSessionStorage from '@dh-io-otb/shared/src/composables/request-session.composable';

import { SlideInformation } from '@dh-io-otb/shared/src/models/entities/text-carousel.model';
import { HiddenTitleStyles } from '@dh-io-otb/shared/src/models/entities/location-box.model';

import DatetimePickerService from '../../services/datetimePicker.service';
import CalendarService from '@dh-io-otb/shared/src/services/calendar.service';
import Viewer360Service from '@dh-io-otb/shared/src/services/viewer360.service';
import { ROUTER, SPECIFIC_ROUTE } from '../../models/constants';
import DateService from '@dh-io-otb/shared/src/services/date.service';
import { TestDriveMode } from '@dh-io-otb/shared/src/models/utilities/testdrive-mode.enum';

export default defineComponent({
  name: 'otb-main-header',
  props: {
    title: {
      type: String,
    },
    goToHome: {
      type: String,
    },
    modelName: {
      type: String,
    },
    vehicleImageLg: {
      type: String,
    },
    vehicleImageSm: {
      type: String,
    },
    expandedMode: {
      type: Boolean,
    },
    shouldShowLink: {
      type: Boolean,
    },
    shouldShowHeader: {
      type: Boolean,
    },
    shouldShowSearch: {
      type: Boolean,
    },
    shouldShowHeaderTooltip: {
      type: Boolean,
    },
    shouldShowReturn: {
      type: Boolean,
    },
  },
  components: {
    FiltersMenu,
    FiltersModal,
    LocationBox,
    DatetimePicker,
  },

  setup(props, { emit }) {
    const { commit, dispatch, getter, state } = useStore();
    const {
      calculateNavTopPosition,
      navPositionScrollEvent,
      navTopStyle,
      navTopStyleFixedDealer,
      otbHeader,
      otbHeaderReturn,
      otbSearchBar,
    } = useNav();
    const { calculateFooterTopPosition, footerPositionScrollEvent, footerTopStyle } = useFooterPosition();

    const {
      metaParams,
      nextSpecificRoute,
      selectLocation: _selectLocation,
      deselectLocation,
      path,
      previous,
      removeFixedDealer,
      checkDealerParameters,
    } = useRoutingApplication();
    const { calendarType, hiddenTitleStyles } = useUtils();
    const { isMobile, isMq3, isMq4, isSmallScreen } = useMediaQuery();
    const { trackShowFilters } = useMainHeader();
    const { clearRequestInSession } = useRequestSessionStorage();

    const selectedDate = ref({});
    const hasSelectedDate = ref(false);
    const activeCalendarSlide = ref(0);
    const dealerCalendar = ref({ availableDates: [''], closedDates: [''] });
    const showFiltersMenu = ref(false);
    const showDetailsModal = ref(false);
    const hasOpenedFiltersModal = ref(false);

    const toggleFiltersModalAndMenu = (): void => {
      toggleFiltersMenu(false);
      toggleFiltersModal();
    };

    const tooltipPosition = computed(() => {
      return isSmallScreen.value ? 'bottom' : 'right';
    });

    const isTestDriveFromHomeEnabled = computed(() => getter?.isTestDriveFromHomeEnabled);

    const searchActionsIcon = computed(() => {
      return isInRequestPage.value ? 'bds/pencil/24' : 'bds/eye/24';
    });

    const showFiltersModalState = computed(() => {
      return state.otbcFlow.showFiltersModal;
    });

    const isFilterSelected = computed(() => {
      return state.otbcFlow.isFilterSelected;
    });

    const isVehicleFilterConfirmed = computed(() => {
      return state.otbcFlow.isVehicleFilterConfirmed;
    });

    const headerCta = computed(() => {
      return isBookingActive.value ? i18n.t('header.search.cta') : i18n.t('header.requestOnly.proceed');
    });

    const backButtonText = computed(() => {
      return state.otbcFlow.skippedResultsPage ? i18n.t('header.backToStart') : i18n.t('header.backToResults');
    });

    const shouldShowVehiclePreferencesButton = computed((): boolean => {
      return !isVehicleFilterConfirmed.value && path.value === ROUTER.REQUEST && isSmallScreen.value;
    });

    const toggleFiltersModal = (): void => {
      hasOpenedFiltersModal.value = true;
      let showFiltersModalValue = state.otbcFlow.showFiltersModal;
      showFiltersModalValue = !showFiltersModalValue;
      commit('showFiltersModal', showFiltersModalValue);
    };

    const locationInputRoute = computed((): boolean => {
      return path.value === ROUTER.SCHEDULE;
    });

    const showDetailsButton = computed((): boolean => {
      return path.value === ROUTER.PERSONAL_DATA || path.value === ROUTER.REQUEST;
    });

    const shouldShowReturnToModelLink = computed((): boolean => {
      return props.shouldShowLink || (!state.otbcFlow.fixedDealer && !state.configuration.bookingActive);
    });

    const shouldShowBackToResultsLink = computed((): boolean => {
      const fixedDealerSkippedToRequest =
        path?.value === ROUTER.REQUEST && state.otbcFlow.fixedDealer && state.otbcFlow.skippedResultsPage;
      return state.configuration.bookingActive && props.shouldShowReturn && !fixedDealerSkippedToRequest;
    });

    const isBookingActive = computed((): boolean => {
      return state.configuration.bookingActive;
    });

    const showDate = computed((): boolean => {
      return hasSelectedDate.value || isPersonalDataRoute.value || hasFixedDealer.value;
    });

    const isPersonalDataRoute = computed((): boolean => {
      return path.value === ROUTER.PERSONAL_DATA;
    });

    const findBookingsButtonEnable = computed(() => {
      return locationSelected.value && state.otbcFlow.locationRadius && locationSelected.value.length > 0;
    });

    const filtersButtonEnable = computed(() => {
      return (
        (isSmallScreen.value && state.otbcFlow.bookingSearchFinished) ||
        (state.otbcFlow.bookingSearchFinished && state.otbcFlow.bookingsSearchResults.length)
      );
    });

    const filtersButtonStatus = computed(() => {
      return !filtersButtonEnable.value && !props.expandedMode && !showDetailsButton.value;
    });

    const selectedInfo = computed((): string => {
      return isPersonalDataRoute.value ? dealerName.value : locationSelected.value;
    });

    const locationSelected = computed(() => {
      return state.otbcFlow.initialLocation?.label || state.otbcFlow.locationQueryFreeText;
    });

    const isInRequestPage = computed(() => {
      return path?.value === ROUTER.REQUEST;
    });

    const isRequestOnTablet = computed((): boolean => {
      return isInRequestPage.value && isSmallScreen.value;
    });

    const isHandheldDevice = computed((): boolean => {
      return isMobile.value || isRequestOnTablet.value;
    });

    const shouldShowSearchContentLogic = computed(() => {
      return (
        props.shouldShowSearch &&
        (isHandheldDevice.value || (!isHandheldDevice.value && !showDetailsButton.value)) &&
        !shouldShowVehiclePreferencesButton.value
      );
    });

    const showMobileDesign = computed(() => {
      return isMobile.value || isMq3.value || isMq4.value;
    });

    const showVehicleDetails = computed(() => {
      return props.shouldShowSearch && isHandheldDevice.value && path.value === ROUTER.REQUEST;
    });

    const showVehicleSmallImage = computed(() => {
      return props.vehicleImageSm && Viewer360Service.is360Image(props.vehicleImageSm) && props.modelName;
    });

    const shouldShowDealerHeader = computed(() => {
      const isSchedulePageInSmallSreen = path?.value === ROUTER.SCHEDULE && isSmallScreen.value;
      const skippedToRequestPage = path?.value === ROUTER.REQUEST && state.otbcFlow.skippedResultsPage;
      return hasFixedDealer.value && (isSchedulePageInSmallSreen || skippedToRequestPage);
    });

    const shouldShowDealerNameOnBottom = computed(() => {
      return (
        hasFixedDealer.value &&
        ((path.value === ROUTER.REQUEST && !state.otbcFlow.skippedResultsPage) || path.value === ROUTER.PERSONAL_DATA)
      );
    });

    const hasFixedDealer = computed(() => {
      return state.otbcFlow.fixedDealer;
    });

    const dateSelected = computed(() => {
      const formattedDate = DateService.formatDate(state.configuration.locale, state.otbcFlow.selectedDay, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
        .split('/')
        .join('.');

      const selectedPeriodLabel = state.otbcFlow.selectedPeriod.label;

      return selectedPeriodLabel && path.value === ROUTER.PERSONAL_DATA
        ? `${formattedDate}, ${selectedPeriodLabel}`
        : formattedDate;
    });

    const filterCounter = computed(() => {
      let count = 0;
      const filterNames = [
        'selectedFuelType',
        'selectedTransmission',
        'selectedMotorisation',
        'selectedTimePeriod',
        'selectedDriveType',
        'selectedWheelbase',
        'selectedWeight',
      ];
      filterNames.forEach((filterName) => {
        if (state.otbcFlow[filterName]) count++;
      });
      const selectedDealers = state.otbcFlow.selectedDealerIds.length;
      if (selectedDealers) count += selectedDealers;
      return count;
    });

    const filtersSelectedString = computed(() => {
      let filtersString = '';
      confirmedFilters.value.forEach((filterKey, index) => {
        const filterName = state.otbcFlow[filterKey].name || getFilterName(filterKey);
        if (index) filtersString += ', ';
        filtersString += filterName;
      });
      if (!filtersString.length) {
        filtersString = state.otbcFlow.model.name;
      }
      return filtersString;
    });

    const confirmedFilters = computed(() => {
      let filters: Array<any> = [];
      const confirmedFiltersKeys = ['confirmedFuelType', 'confirmedTransmission', 'confirmedMotorisation'];
      confirmedFiltersKeys.forEach((filterKey) => {
        if (state.otbcFlow[filterKey]) {
          filters.push(filterKey);
        }
      });
      return filters;
    });

    const getFilterName = (filterKey: string) => {
      const availableFiltersKeysMap = {
        confirmedFuelType: 'availableFuelOptions',
        confirmedTransmission: 'availableTransmissionOptions',
        confirmedMotorisation: 'availableMotorisationOptions',
      };
      const filterValue = state.otbcFlow[filterKey];
      const filterOptions = state.otbcFlow[availableFiltersKeysMap[filterKey]];
      const filterOption = filterOptions.find((filter) => filter.value === filterValue);
      return filterOption?.text;
    };

    const dynamicClass = (specificClass: string): string => {
      return props.expandedMode ? `${specificClass}-expanded` : `${specificClass}`;
    };

    const toggleFiltersMenu = (state?: boolean): void => {
      showFiltersMenu.value = state ?? !showFiltersMenu.value;
      emit('toggleFiltersMenu', showFiltersMenu.value);
      commit('showFiltersMenu', showFiltersMenu.value);
    };

    const toggleDetailsOrFiltersModal = (): void => {
      if (!isBookingActive.value || path?.value === ROUTER.REQUEST) return toggleFiltersModal();
      toggleDetailsModal(true);
    };

    const toggleDetailsModal = (state?: boolean): void => {
      showDetailsModal.value = state ?? !showDetailsModal.value;
      commit('showDetailsModal', showDetailsModal.value);
    };

    const goToHomeEvent = () => {
      nextSpecificRoute(SPECIFIC_ROUTE.VMOS);
    };

    const toggleSectionOnClick = (): void => {
      if (path?.value === ROUTER.REQUEST) return toggleFiltersModalAndMenu();
      if (!isBookingActive.value && isFilterSelected.value) return toggleFiltersModal();
      if (path.value === ROUTER.PERSONAL_DATA || state.otbcFlow.testDriveMode === TestDriveMode.REQUEST)
        return toggleDetailsModal(true);
      toggleFiltersMenu(true);
      trackShowFilters();
    };

    const changeDate = (day: Date | null): void => {
      hasSelectedDate.value = !!day;
      DatetimePickerService.commitSelectedDay(day);
      selectedDate.value = { start: day };
      commit('searchedDate', day);
      if (path.value != ROUTER.DEFINE_LOCATION && state.otbcFlow.locationSelected) {
        commit('bookingSearchFinished', false);
        dispatch(
          hasFixedDealer.value
            ? 'getTestDriveBookingVehicleSearchFixedDealer'
            : 'getTestDriveBookingVehicleSearchWithinRadius',
        );
      }
    };

    const onReturn = (): void => {
      const skippedToRequestPage = path?.value === ROUTER.REQUEST && state.otbcFlow.skippedResultsPage;
      if (hasFixedDealer.value && (path?.value === ROUTER.SCHEDULE || skippedToRequestPage)) {
        if (state.otbcFlow.referrer) {
          //Go to previous site
          window.location.href = state.otbcFlow.referrer;
        } else {
          nextSpecificRoute(SPECIFIC_ROUTE.VMOS);
        }
      }
      if (state.otbcFlow.skippedResultsPage && path?.value === ROUTER.REQUEST) {
        deselectLocation();
        changeDate(null);
        cleanRadius();
        return;
      }
      previous();
    };

    const cleanRadius = (): void => {
      const dealerRadiusArray = state.configuration.dealerRadiusArray;
      const dealerRadius = state.configuration.dealerRadius;
      dispatch('selectLocationRadius', {
        locationRadius: `${dealerRadiusArray[Math.floor((dealerRadiusArray.length - 1) / 2)]}${dealerRadius.unit}`,
      });
    };

    const hiddenTitle = computed((): HiddenTitleStyles => {
      return hiddenTitleStyles(!props.expandedMode);
    });

    const localeCode = computed((): string => getter.localeCode);

    const dealerName = computed((): string => state.otbcFlow.selectedDealer?.computedName);

    const noBookingResults = computed(
      (): boolean => !state.otbcFlow.filteredBookingsSearchResults.length && state.otbcFlow.bookingSearchFinished,
    );

    watch(
      () => getter.initialUnavailableDays,
      () => {
        dealerCalendar.value = CalendarService.buildGenericCalendar(getter.initialUnavailableDays);
      },
    );

    watch(
      () => state.otbcFlow.fixedDealer,
      (newValue) => {
        if (!newValue) {
          removeFixedDealer();
        }
      },
    );

    watch(
      () => state.otbcFlow.searchedDate,
      (newDay) => {
        selectedDate.value = { start: newDay };
      },
    );

    onMounted((): void => {
      navPositionScrollEvent();
      footerPositionScrollEvent();
      if (checkDealerParameters) checkDealerParameters();
    });

    watch(
      () => state.otbcFlow.dealer,
      () => {
        if (checkDealerParameters) checkDealerParameters();
      },
    );

    onUpdated((): void => {
      setTimeout(() => {
        calculateNavTopPosition();
        calculateFooterTopPosition();
      }, 100);
    });

    const changeSheet = (page: SlideInformation): void => {
      activeCalendarSlide.value = page.index;
    };

    watch(
      () => getter.initialUnavailableDays,
      () => {
        dealerCalendar.value = CalendarService.buildGenericCalendar(getter.initialUnavailableDays);
      },
    );

    const selectLocation = (): void => {
      clearRequestInSession();
      _selectLocation && _selectLocation();
    };

    return {
      // METHODS
      toggleDetailsOrFiltersModal,
      changeDate,
      changeSheet,
      dynamicClass,
      goToHomeEvent,
      nextSpecificRoute,
      selectLocation,
      toggleDetailsModal,
      toggleFiltersMenu,
      toggleSectionOnClick,
      onReturn,
      toggleFiltersModalAndMenu,
      toggleFiltersModal,
      // COMPUTEDS
      isTestDriveFromHomeEnabled,
      isBookingActive,
      dateSelected,
      findBookingsButtonEnable,
      filterCounter,
      filtersButtonEnable,
      filtersButtonStatus,
      hiddenTitle,
      isHandheldDevice,
      isMobile,
      showMobileDesign,
      tooltipPosition,
      localeCode,
      locationInputRoute,
      locationSelected,
      navTopStyle,
      navTopStyleFixedDealer,
      otbHeader,
      otbHeaderReturn,
      otbSearchBar,
      footerTopStyle,
      showDetailsButton,
      shouldShowSearchContentLogic,
      isInRequestPage,
      showVehicleDetails,
      showVehicleSmallImage,
      filtersSelectedString,
      noBookingResults,
      dealerName,
      isPersonalDataRoute,
      showDate,
      selectedInfo,
      shouldShowReturnToModelLink,
      shouldShowBackToResultsLink,
      hasFixedDealer,
      shouldShowDealerNameOnBottom,
      shouldShowDealerHeader,
      showFiltersModalState,
      isFilterSelected,
      isVehicleFilterConfirmed,
      shouldShowVehiclePreferencesButton,
      headerCta,
      searchActionsIcon,
      backButtonText,
      isSmallScreen,

      // REFS
      activeCalendarSlide,
      dealerCalendar,
      hasSelectedDate,
      previous,
      showDetailsModal,
      showFiltersMenu,
      calendarType,
      selectedDate,
      hasOpenedFiltersModal,
    };
  },
});
</script>

<style lang="scss" src="./main-header.scss" scoped></style>
